
import React, { JSX } from 'react'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Footer from 'components/Footer'
import { SHOPIFY_CONNECTED_PATH } from 'lib/paths'

export { SHOPIFY_CONNECTED_PATH }

interface ErrorRow {
  code: string
  title: string
  body: string
}

const LINK_EXPIRED_BODY =
  'That install link had expired or had already been used. Nothing changed on your store, ask us for a fresh link and it will take one click.'

const OUR_SIDE_BODY =
  'The install did not finish, and the problem is on our side. Nothing changed on your store. Please try the link again, or let us know and we will send a new one.'

const ERROR_ROWS: ErrorRow[] = [
  { code: 'app_unknown', title: 'That install link is no longer valid', body: 'We could not match this link to an app. Nothing changed on your store, ask us for a fresh install link.' },
  { code: 'shop_invalid', title: 'We could not read the store address', body: OUR_SIDE_BODY },
  { code: 'hmac_invalid', title: 'We could not verify that request', body: 'The request did not arrive with a valid signature from Shopify, so we stopped. Nothing changed on your store. Please start again from the install link we sent you.' },
  { code: 'public_track_not_enabled', title: 'This app is not open for public installs yet', body: 'Installs are currently by invitation only. Nothing changed on your store, ask us for an install link.' },
  { code: 'app_unbound', title: 'This install link is not finished on our side', body: 'The link works, but it is not yet pointed at an account here. Nothing changed on your store, let us know and we will finish the setup and resend it.' },
  { code: 'start_failed', title: 'The install could not be started', body: OUR_SIDE_BODY },
  { code: 'state_missing', title: 'That install link expired', body: LINK_EXPIRED_BODY },
  { code: 'state_invalid', title: 'That install link expired', body: LINK_EXPIRED_BODY },
  { code: 'state_expired', title: 'That install link expired', body: LINK_EXPIRED_BODY },
  { code: 'shop_mismatch', title: 'That link was issued for a different store', body: 'The store that approved the install is not the one this link was created for. Nothing changed on either store, please start again from the link we sent you.' },
  { code: 'denied', title: 'The install was cancelled', body: 'The permission request was declined, so nothing was installed and nothing changed on your store. You can start again from the same link any time while it is valid.' },
  { code: 'code_missing', title: 'The install did not finish', body: OUR_SIDE_BODY },
  { code: 'exchange_failed', title: 'The install did not finish', body: OUR_SIDE_BODY },
  { code: 'storage_failed', title: 'The install did not finish', body: OUR_SIDE_BODY },
  { code: 'scope_insufficient', title: 'Some permissions were not granted', body: 'We need read access to products and orders to show sales on the stream, and the install completed without them. Nothing changed on your store, start again from the link and approve all of the requested permissions.' }
]

const GENERIC_ERROR_ROW: ErrorRow = {
  code: 'unknown',
  title: 'The install did not finish',
  body: OUR_SIDE_BODY
}

const errorRowFor = (code: string | null): ErrorRow => {
  if (code === null) return GENERIC_ERROR_ROW
  return ERROR_ROWS.find((row) => row.code === code) ?? GENERIC_ERROR_ROW
}

const Shell = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <div className='flex min-h-screen flex-col bg-dm-bg-main text-dm-text-primary'>
    <div className='mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-6 py-16'>
      <div className='rounded-xl border border-white/10 bg-dm-bg-secondary p-8'>
        {children}
      </div>
    </div>
    <Footer />
  </div>
)

const DataNote = (): JSX.Element => (
  <p className='mt-6 border-t border-white/10 pt-4 text-xs text-dm-text-accent'>
    StagePilot reads product and order data only. Customer names, email
    addresses and shipping addresses are never requested, never received,
    and never stored.
  </p>
)

const PcdWarning = (): JSX.Element => (
  <div
    data-testid='shopify-connected-pcd-warn'
    className='mt-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300'
  >
    <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
    <span>
      One thing is still pending on our side: Shopify has not yet approved
      our app to receive this store&apos;s order data, so sales will not
      appear on the stream until that clears. No user input needed at this time.
    </span>
  </div>
)

const ConnectedState = ({ pcdPending }: { pcdPending: boolean }): JSX.Element => (
  <div data-testid='shopify-connected-success'>
    <CheckCircleIcon className='size-10 text-emerald-400' aria-hidden='true' />
    <h1 className='mt-4 text-2xl font-semibold text-dm-text-primary'>Your store is connected</h1>
    <p className='mt-3 text-sm text-dm-text-secondary'>
      StagePilot can now show this store&apos;s sales live on the stream.
      There is nothing else to set up, and nothing to change inside your
      Shopify admin. You can close this window.
    </p>
    {pcdPending && <PcdWarning />}
    <p className='mt-4 text-sm text-dm-text-secondary'>
      To disconnect at any time, remove the app from your Shopify admin.
      Sales simply stop appearing; nothing else about your store changes.
    </p>
    <DataNote />
  </div>
)

const ErrorState = ({ row }: { row: ErrorRow }): JSX.Element => (
  <div data-testid='shopify-connected-error'>
    <ExclamationTriangleIcon className='size-10 text-amber-400' aria-hidden='true' />
    <h1 className='mt-4 text-2xl font-semibold text-dm-text-primary'>{row.title}</h1>
    <p className='mt-3 text-sm text-dm-text-secondary'>{row.body}</p>
    <p className='mt-4 text-xs text-dm-text-accent'>
      Reference: <span className='font-mono'>{row.code}</span>
    </p>
    <DataNote />
  </div>
)

export const ShopifyConnectedPage = (): JSX.Element => {
  const params = new URLSearchParams(globalThis.location.search)
  const connected = params.get('connected') === '1'
  const pcdPending = params.get('warn') === 'pcd'

  if (connected) {
    return (
      <Shell>
        <ConnectedState pcdPending={pcdPending} />
      </Shell>
    )
  }
  return (
    <Shell>
      <ErrorState row={errorRowFor(params.get('error'))} />
    </Shell>
  )
}
